'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, t } from '@lingui/macro';
import { Button, Stack } from '@mui/material';
import { PasswordInput, TextFieldInput } from '@prismo-io/design-system';
import { useRouter } from 'next/navigation';
import { type FC, useEffect, useTransition } from 'react';
import { type DeepPartial, type SubmitHandler, useForm } from 'react-hook-form';
import { useSignInWithPassword } from '../../../hooks';
import { SignInSchema, type SignInSchemaT } from './schema';

type SignInFormProps = {
  successUrl: string;
  defaultValues?: DeepPartial<SignInSchemaT>;
  onEmailChange?: (email: string) => void;
  onSignInError: (isError: boolean) => void;
};

const DEFAULT_VALUES: SignInSchemaT = {
  email: '',
  password: '',
};

export const SignInForm: FC<SignInFormProps> = (props) => {
  const {
    defaultValues = {},
    successUrl,
    onEmailChange,
    onSignInError,
  } = props;

  const { trigger: signIn, isMutating } = useSignInWithPassword();

  const router = useRouter();
  const [isTransiting, startTransition] = useTransition();

  const onSubmit: SubmitHandler<SignInSchemaT> = (data) => {
    startTransition(() => {
      signIn(data, {
        onError() {
          onSignInError(true);
        },
        onSuccess() {
          router.push(successUrl);
        },
      });
    });
  };

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm<SignInSchemaT>({
    mode: 'all',
    resolver: zodResolver(SignInSchema()),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const emailWatched = watch('email');

  useEffect(() => {
    if (onEmailChange && emailWatched) {
      onEmailChange(emailWatched);
    }
  }, [emailWatched, onEmailChange]);

  return (
    <Stack
      spacing={2}
      marginY={4}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <TextFieldInput
        control={control}
        disabled={!!defaultValues['email']}
        name="email"
        label={t`Email`}
        type="email"
        autoComplete="email"
        size="small"
        required
      />
      <PasswordInput
        control={control}
        name="password"
        label={t`Mot de passe`}
        size="small"
        required
      />

      <Button
        type="submit"
        disabled={!isValid || isTransiting || isMutating}
        variant="contained"
        color="primary"
      >
        <Trans>Connexion</Trans>
      </Button>
    </Stack>
  );
};
